import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { RegisterSessionEnum, ScheduleStructure } from '@schooly/api';
import {
  CheckIconV2,
  DayPicker,
  DayPickerOption,
  ItemComponentProps,
  ScheduleDayGrid,
  ScheduleGridItemContent,
  ScheduleGridItemData,
  ScheduleGridTooltip,
  TooltipComponentProps,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { isSameDay } from 'date-fns';
import { FC, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { findPointForTarget } from '../../../Attendance/AttendanceTable/common/trackingPoints';
import { AttendanceGridTrackingPoint } from '../../../Attendance/AttendanceTable/common/types';
import {
  ScheduleEndAdornment,
  TimeSlotTooltipContent,
} from '../../../ProfileModal/tabs/ProfileModalSchedule/ProfileModalSchedule';
import { ScheduleItemData } from '../../../ProfileModal/tabs/ProfileModalSchedule/utils';
import { isStructureActiveDay } from './utils';

const TIME_COLUMN_WIDTH = 48;

interface TimetableWidgetProps {
  weekDays: DayPickerOption<Date>[];
  selectedDay: Date;
  onSelectDay: (day: Date) => void;
  dayItems: ScheduleGridItemData<ScheduleItemData>[];
  pointsByGroupId: Map<string, AttendanceGridTrackingPoint[]>;
  timeRange: { start: string; end: string };
  structure?: ScheduleStructure;
}

export const TimetableWidget: FC<TimetableWidgetProps> = ({
  weekDays,
  selectedDay,
  onSelectDay,
  dayItems,
  pointsByGroupId,
  timeRange,
  structure,
}) => {
  const today = useMemo(() => newDateTimezoneOffset(), []);
  const isTodaySelected = isSameDay(selectedDay, today);
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const nowIndicatorRef = useRef<HTMLDivElement>(null);

  const hasScheduleItems = dayItems.length > 0;

  const renderItem = useCallback(
    (props: ItemComponentProps<ScheduleItemData>) => (
      <WidgetItemContent {...props} pointsByGroupId={pointsByGroupId} />
    ),
    [pointsByGroupId],
  );

  const renderTooltip = useCallback(
    (props: TooltipComponentProps<ScheduleItemData>) => (
      <WidgetScheduleTooltip {...props} pointsByGroupId={pointsByGroupId} />
    ),
    [pointsByGroupId],
  );

  const emptyStubLabelId = useMemo((): string | null => {
    if (hasScheduleItems) return null;

    if (!structure) return 'dashboard-teacher-Timetable-NoTimetable';

    const isWorkingDay = isStructureActiveDay(structure.weekdays, selectedDay);

    return isWorkingDay
      ? 'dashboard-teacher-Timetable-NoLessons'
      : 'dashboard-teacher-Timetable-NonWorkingDay';
  }, [hasScheduleItems, selectedDay, structure?.weekdays, structure]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedDay triggers auto-scroll on day change
  useLayoutEffect(() => {
    const el = scheduleScrollRef.current;
    if (!el) return;

    const id = requestAnimationFrame(() => {
      const indicator = nowIndicatorRef.current;
      if (!indicator) {
        el.scrollTop = 0;
        return;
      }

      const top = indicator.offsetTop;
      const halfView = el.clientHeight / 2;
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.scrollTop = Math.min(Math.max(0, top - halfView), maxScroll);
    });

    return () => cancelAnimationFrame(id);
  }, [selectedDay, isTodaySelected, hasScheduleItems]);

  return (
    <>
      <Box
        sx={(theme) => ({
          padding: theme.spacing(2, 2.5, 1),
          borderBottom: theme.mixins.borderValue(),
        })}
      >
        <DayPicker
          days={weekDays}
          selectedValue={selectedDay}
          onSelect={onSelectDay}
          isEqual={isSameDay}
          shouldShowIndicator={(value) => isSameDay(value, today)}
        />
      </Box>

      <Box
        ref={scheduleScrollRef}
        sx={(theme) => ({
          padding: theme.spacing(0.25, 2.5, 2.5, 1),
          maxHeight: 540,
          overflow: hasScheduleItems ? 'auto' : 'hidden',
          position: 'relative',
        })}
      >
        <ScheduleDayGrid<ScheduleItemData>
          items={dayItems}
          timeRange={timeRange}
          showNowIndicator={isTodaySelected && hasScheduleItems}
          nowIndicatorProps={{ ref: nowIndicatorRef }}
          ItemComponent={renderItem}
          TooltipComponent={renderTooltip}
          timeColumnProps={{
            sx: {
              borderRight: 'none',
              '& ::before': { display: 'none' },
              width: TIME_COLUMN_WIDTH,
            },
            hideLastLabel: false,
          }}
        />

        {emptyStubLabelId && <TimetableEmptyStub labelId={emptyStubLabelId} />}
      </Box>
    </>
  );
};

type SlotAttendance = 'completed' | 'required';

const findScheduleSlotPoint = (
  points: AttendanceGridTrackingPoint[],
  periodId: string,
  registerSession: RegisterSessionEnum | null,
): AttendanceGridTrackingPoint | undefined => {
  const slotPoint = findPointForTarget({ periodId, session: registerSession }, points);
  const periodPoint = registerSession
    ? findPointForTarget({ periodId, session: null }, points)
    : undefined;
  const sessionPoint = registerSession
    ? findPointForTarget({ periodId: null, session: registerSession }, points)
    : undefined;

  return slotPoint ?? periodPoint ?? sessionPoint;
};

const groupSlotAttendance = (
  points: AttendanceGridTrackingPoint[] | undefined,
  periodId: string,
  registerSession: RegisterSessionEnum | null,
): SlotAttendance | null => {
  if (!points) return null;

  const match = findScheduleSlotPoint(points, periodId, registerSession);
  if (!match) return null;

  return match.complete ? 'completed' : 'required';
};

const slotAttendance = (
  groups: ScheduleItemData['groupsWithRoom'],
  periodId: string,
  registerSession: RegisterSessionEnum | null,
  pointsByGroupId: Map<string, AttendanceGridTrackingPoint[]>,
): SlotAttendance | null => {
  let hasCompleted = false;

  for (const group of groups) {
    const status = groupSlotAttendance(pointsByGroupId.get(group.id), periodId, registerSession);
    if (status === 'required') return 'required';
    if (status === 'completed') hasCompleted = true;
  }

  return hasCompleted ? 'completed' : null;
};

const WidgetItemContent: FC<
  ItemComponentProps<ScheduleItemData> & {
    pointsByGroupId: Map<string, AttendanceGridTrackingPoint[]>;
  }
> = ({ pointsByGroupId, item, ...props }) => {
  const attendance = slotAttendance(
    item.data.groupsWithRoom,
    item.data.periodId,
    item.data.registerSession ?? null,
    pointsByGroupId,
  );

  const itemWithMarkers = attendance
    ? {
        ...item,
        endAdornment: ({ size }: { size: (typeof props)['size'] }) => (
          <ScheduleEndAdornment
            markers={<AttendanceSlotMarker completed={attendance === 'completed'} />}
            roomLabel={item.data.roomLabel}
            staffLabel={item.data.staffLabel}
            size={size}
          />
        ),
      }
    : item;

  return (
    <Box sx={{ height: '100%', ml: 0.5 }}>
      <ScheduleGridItemContent
        {...props}
        item={itemWithMarkers}
        labelSx={(theme) => ({
          fontSize: theme.typography.h3.fontSize,
          fontWeight: theme.typography.h3.fontWeight,
        })}
      />
    </Box>
  );
};

const AttendanceSlotGlyph: FC<{ completed: boolean }> = ({ completed }) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      border: '1px solid white',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: completed ? 'success.superLight' : 'common.orange5',
      color: 'success.main',
      '--icon-size': '10px',
    }}
  >
    {completed ? (
      <CheckIconV2 />
    ) : (
      <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'common.orange' }} />
    )}
  </Box>
);

const AttendanceSlotMarker: FC<{ completed: boolean }> = ({ completed }) => {
  const { formatMessage } = useIntl();

  return (
    <Tooltip
      arrow={false}
      title={
        <Typography color="common.grey2" variant="caption">
          {formatMessage({
            id: completed
              ? 'dashboard-teacher-SlotAttendanceCompleted'
              : 'dashboard-teacher-SlotAttendanceRequired',
          })}
        </Typography>
      }
      componentsProps={{
        tooltip: {
          sx: {
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            margin: '2px !important',
          },
        },
      }}
    >
      <Box
        component="span"
        onMouseOver={(event) => event.stopPropagation()}
        sx={{ display: 'inline-flex', flexShrink: 0 }}
      >
        <AttendanceSlotGlyph completed={completed} />
      </Box>
    </Tooltip>
  );
};

const WidgetScheduleTooltip: FC<
  TooltipComponentProps<ScheduleItemData> & {
    pointsByGroupId: Map<string, AttendanceGridTrackingPoint[]>;
  }
> = ({ item, pointsByGroupId, children }) => {
  return (
    <ScheduleGridTooltip
      content={
        <TimeSlotTooltipContent
          startTime={item.startTime}
          endTime={item.endTime}
          periodType={item.data.periodType}
          session={item.data.registerSession}
          groupsWithRoom={item.data.groupsWithRoom}
          onGroupClick={item.data.onGroupClick}
          renderGroupMarker={(groupId) => {
            const status = groupSlotAttendance(
              pointsByGroupId.get(groupId),
              item.data.periodId,
              item.data.registerSession ?? null,
            );
            if (!status) return null;
            return <AttendanceSlotGlyph completed={status === 'completed'} />;
          }}
        />
      }
    >
      {children}
    </ScheduleGridTooltip>
  );
};

const TimetableEmptyStub: FC<{ labelId: string }> = ({ labelId }) => {
  const { formatMessage } = useIntl();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      sx={{ position: 'absolute', left: 0, right: 0, top: 0, ml: `${TIME_COLUMN_WIDTH}px`, px: 1 }}
    >
      <Typography variant="h2" sx={{ mb: 0.5, textAlign: 'center' }}>
        {formatMessage({ id: `${labelId}-title` })}
      </Typography>
      <Typography variant="h3" color="common.grey2" sx={{ textAlign: 'center' }}>
        {formatMessage({ id: `${labelId}-subtitle` })}
      </Typography>
    </Stack>
  );
};
